import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import { memberExportUrl } from '@/features/csv/api';
import { fetchInvitations } from '@/features/invitations/api';
import { PendingInvitations } from '@/features/invitations/PendingInvitations';
import {
  MEMBER_STATUS_LABELS,
  fetchMembers,
  type Member,
  type MemberStatus,
} from '@/features/members/api';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { fetchStaff, staffLabel } from '@/features/staff/api';
import { ApiError } from '@/lib/api/http';

const PER_PAGE = 20;

/**
 * 一覧の列（D-313）。会員ID は事務局が採番する管理番号なので、受講生同士の
 * 相互閲覧（D-105）では出さない。ほかの列は全ロール共通。
 */
function columnsFor(showMemberId: boolean): Column<Member>[] {
  return [
    {
      key: 'name',
      header: '氏名',
      primary: true,
      // 当日その場で本人を確認できるよう、氏名の横に顔写真を並べる（D-053）
      render: (member) => (
        <span className="flex items-center gap-2">
          <PhotoPreview photoUrl={member.photo_url} name={member.name} size={32} />
          {member.name}
        </span>
      ),
    },
    ...(showMemberId
      ? [{ key: 'member_id', header: '会員ID', render: (member: Member) => member.member_id }]
      : []),
    { key: 'company', header: '企業名', render: (member) => member.company ?? '—' },
    { key: 'position', header: '役職', render: (member) => member.position ?? '—' },
    { key: 'specialty', header: '管掌領域', render: (member) => member.specialty ?? '—' },
    { key: 'cohort', header: '期', render: (member) => member.cohort_name ?? '—' },
    { key: 'course', header: 'コース', render: (member) => member.course_name ?? '—' },
    {
      key: 'status',
      header: 'ステータス',
      render: (member) => MEMBER_STATUS_LABELS[member.status],
    },
  ];
}

/** 会員一覧。M2 の共通部品（ListTable）を使い回す。 */
export function MemberListPage(): ReactElement {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOffice = user?.active_role === 'office';
  const isDirector = user?.active_role === 'director';
  const isSuccess = user?.active_role === 'success';
  const isStudent = user?.active_role === 'student';
  const isStaffViewer = isDirector || isSuccess;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<MemberStatus | null>(null);
  // 「担当している会員を見る」からの絞り込み（D-078）
  const assignedTo = searchParams.get('assigned_to');
  // サクセス担当一覧からの絞り込みは、面談実施回だけに絞る（D-171）
  const successMeetingsOnly = searchParams.get('success_meetings_only') === 'true';

  const query = useQuery({
    queryKey: ['members', page, search, status, assignedTo, successMeetingsOnly],
    queryFn: () =>
      fetchMembers({
        page,
        per_page: PER_PAGE,
        q: search,
        status,
        assigned_to: assignedTo,
        success_meetings_only: successMeetingsOnly,
      }),
  });

  // 絞り込み中の担当者名を出すためだけの取得（事務局しか /staff を引けない）
  const staffQuery = useQuery({
    queryKey: ['staff', null],
    queryFn: () => fetchStaff(),
    enabled: Boolean(assignedTo) && isOffice,
  });
  const assignee = staffQuery.data?.find((s) => s.user_id === assignedTo);

  // 招待中（まだ受諾されていない）受講生（D-140・D-153）。理事・サクセスの一覧と同じ扱い
  const invitationsQuery = useQuery({
    queryKey: ['invitations', 'member-pending'],
    queryFn: () => fetchInvitations(1, 100),
    enabled: isOffice,
  });
  const pendingInvitations = (invitationsQuery.data?.items ?? []).filter(
    (i) => i.status === 'pending' && i.role === 'student',
  );

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  const total = query.data?.total ?? 0;

  return (
    <>
      <PageHeader
        title={isDirector || isSuccess ? '関わった会員' : isStudent ? '受講生一覧' : '会員'}
        description={
          isDirector || isSuccess
            ? '担当したイベントの対象受講生です。「詳細」でプロフィール（公開項目）を、「年間ポートフォリオ」で提出物・出欠・アンケート回答などを確認できます。'
            : isStudent
              ? 'ほかの受講生のプロフィール（公開項目）を確認できます。氏名を押すと詳細が開きます。電話番号と連絡先メールアドレスは表示されません。'
              : '権限に応じて表示できる範囲が変わります。'
        }
        actions={
          isOffice ? (
            <>
              <a
                href={memberExportUrl()}
                className="btn-secondary"
              >
                CSV出力
              </a>
              <button
                type="button"
                onClick={() => navigate('/members/import')}
                className="btn-secondary"
              >
                CSV取込
              </button>
              <button
                type="button"
                onClick={() => navigate('/members/new')}
                className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
              >
                招待する
              </button>
            </>
          ) : undefined
        }
      />
      {isOffice && (
        <PendingInvitations
          invitations={pendingInvitations}
          invalidateKeys={[['invitations', 'member-pending']]}
        />
      )}
      {assignedTo && (
        <Notice kind="info" dismissible={false}>
          {assignee ? staffLabel(assignee) : 'この担当者'}
          {successMeetingsOnly ? 'がサクセス面談を実施した会員だけ' : 'が担当している会員だけ'}
          を表示しています。{' '}
          <button
            type="button"
            onClick={() => {
              setSearchParams({});
              setPage(1);
            }}
            className="text-wpaccent underline"
          >
            絞り込みを解除する
          </button>
        </Notice>
      )}
      <ListTable<Member>
        caption={isStudent ? '受講生一覧' : '会員一覧'}
        rows={query.data?.items ?? []}
        columns={columnsFor(!isStudent)}
        rowKey={(member) => member.id}
        rowLabel={(member) => member.name}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        tabs={[
          { value: null, label: 'すべて', count: status === null ? total : undefined },
          { value: 'active', label: '在籍' },
          { value: 'graduated', label: '卒業' },
          { value: 'withdrawn', label: '退会' },
        ]}
        activeTab={status}
        onTabChange={(value) => {
          setStatus(value as MemberStatus | null);
          setPage(1);
        }}
        searchValue={search}
        searchPlaceholder={isStudent ? '氏名で検索' : '氏名・会員IDで検索'}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        rowActions={[
          { label: '詳細', onSelect: (member) => navigate(`/members/${member.id}`) },
          ...(isStaffViewer
            ? [
                {
                  label: '年間ポートフォリオ',
                  onSelect: (member: Member) => navigate(`/members/${member.id}/portfolio`),
                },
              ]
            : []),
          ...(isOffice
            ? [
                {
                  label: 'プロフィールを編集',
                  onSelect: (member: Member) => navigate(`/members/${member.id}/edit`),
                },
                {
                  label: '連携設定を編集',
                  onSelect: (member: Member) => navigate(`/members/${member.id}/integrations`),
                },
                {
                  label: '参加イベント一覧',
                  onSelect: (member: Member) => navigate(`/members/${member.id}/events`),
                },
              ]
            : []),
        ]}
        page={page}
        perPage={PER_PAGE}
        total={total}
        onPageChange={setPage}
        emptyTitle={isStudent ? '表示できる受講生がいません' : '表示できる会員がいません'}
        emptyDescription="検索条件を変えるか、事務局にお問い合わせください。"
      />
    </>
  );
}
